import React, { useEffect, useState } from "react";
import { Check, Loader, AlertTriangle, Wifi, Package, MapPin, Clock, Euro } from "lucide-react";
import axios from "axios";

const BackendUrl = process.env.REACT_APP_Backend_Url;

const COLORS = {
  orange: "#E87E04", // Orange plus vif
  white: "#FFFFFF",
  green: "#008751", // Vert du drapeau nigérien
  darkGreen: "#006B3F", // Vert foncé pour les hovers
  lightOrange: "#FFE0B2", // Version claire de l'orange pour les backgrounds
  lightGreen: "#CCFFCC", // Version claire du vert pour certains éléments
  gray: "#718096",
};
const ShippingZonesDropdown = ({
  zones,
  zoneId,
  setZoneId,
  prixliv,
  setPrixliv,
  panier,
  setPoidsTotal,
  pp,
}) => {
  const [isOpen, setIsOpen] = useState(true); // Ouvert par défaut
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requestStatus, setRequestStatus] = useState("idle"); // idle, loading, success, error, network

  useEffect(() => {
    const calculateShipping = async () => {
      if (!zoneId || !panier || panier.length === 0) return;

      try {
        setIsLoading(true);
        setRequestStatus("loading");
        setError(null);

        const selectedZone = zones.find((zone) => zone._id === zoneId);
        const countryCode = selectedZone?.countries || "FR"; // Utilise un code pays par défaut si manquant

        const response = await axios.post(
          `${BackendUrl}/api/shipping/calculate`,
          {
            cartItems: panier,
            countryCode,
            pp: pp(),
          }
        );

        if (response.data.success) {
          setPrixliv(response.data.shippingDetails.coutTotal);
          setPoidsTotal(response.data.shippingDetails.poidsTotal);
          localStorage.setItem(
            "shippingDetails",
            JSON.stringify(response.data.shippingDetails)
          );
          console.log(response.data);
          setRequestStatus("success");
        } else {
          console.error("Erreur de calcul :", response.data.message);
          setError(
            response.data.message ||
              "Erreur lors du calcul des frais d'expédition"
          );
          setRequestStatus("error");
          setPrixliv(0);
        }
      } catch (error) {
        console.error("Erreur lors du calcul des frais :", error);
        if (error.message === "Network Error") {
          setRequestStatus("network");
          setError(
            "Problème de connexion internet. Veuillez vérifier votre connexion."
          );
        } else {
          setRequestStatus("error");
          setError(
            error.message || "Erreur lors du calcul des frais d'expédition"
          );
        }
        setPrixliv(0);
      } finally {
        setIsLoading(false);
      }
    };

    calculateShipping();
  }, [zoneId, panier]);

  const renderStatusIndicator = () => {
    switch (requestStatus) {
      case "loading":
        return (
          <div className="flex items-center justify-center p-4 bg-gradient-to-r from-blue-50 to-blue-100 border-l-4 border-blue-400 rounded-lg">
            <Loader className="w-5 h-5 mr-3 animate-spin text-blue-600" />
            <span className="text-blue-800 font-medium">Calcul des frais d'expédition en cours...</span>
          </div>
        );
      case "success":
        return (
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-400 rounded-lg">
            <div className="flex items-center">
              <Check className="w-5 h-5 mr-3 text-green-600" />
              <span className="text-green-800 font-medium">Frais calculés avec succès</span>
            </div>
            <div className="flex items-center text-green-700 font-bold">
              <Euro className="w-4 h-4 mr-1" />
              {prixliv.toFixed(2)} €
            </div>
          </div>
        );
      case "error":
        return (
          <div className="flex items-center justify-center p-4 bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-400 rounded-lg">
            <AlertTriangle className="w-5 h-5 mr-3 text-red-600" />
            <span className="text-red-800 font-medium">{error || "Une erreur est survenue"}</span>
          </div>
        );
      case "network":
        return (
          <div className="flex items-center justify-center p-4 bg-gradient-to-r from-yellow-50 to-yellow-100 border-l-4 border-yellow-400 rounded-lg">
            <Wifi className="w-5 h-5 mr-3 text-yellow-600" />
            <span className="text-yellow-800 font-medium">{error || "Problème de connexion internet"}</span>
          </div>
        );
      default:
        return null;
    }
  };

  if (!zones || zones.length === 0) {
    return (
      <div className="text-center p-8 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
        <Package className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        <p className="text-gray-600 font-medium">Aucune zone d'expédition disponible</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* En-tête */}
      <div className="bg-gradient-to-r from-emerald-600 to-green-600 text-white p-6 rounded-xl shadow-lg">
        <div className="flex items-center">
          <div className="p-3 bg-white/20 rounded-lg mr-4">
            <Package className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-xl font-bold">Options d'expédition</h3>
            <p className="text-emerald-100 mt-1">Choisissez votre zone de livraison</p>
          </div>
        </div>
      </div>

      {/* Options de zones */}
      <div className="grid gap-4">
        {zones.map((zone) => {
          const isSelected = zoneId === zone._id;

          return (
            <div
              key={zone._id}
              className={`group relative border-2 rounded-xl p-5 transition-all duration-300 cursor-pointer hover:shadow-lg hover:scale-[1.02] ${
                isSelected
                  ? "border-emerald-500 bg-emerald-50 shadow-lg scale-[1.02]"
                  : "border-gray-200 bg-white hover:border-emerald-300"
              }`}
              onClick={() => setZoneId(zone._id)}
            >
              {/* Badge de sélection */}
              <div className="absolute -top-2 -right-2">
                <div
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                    isSelected
                      ? "bg-emerald-600 border-emerald-600"
                      : "bg-white border-gray-300 group-hover:border-emerald-400"
                  }`}
                >
                  {isSelected && <Check className="w-4 h-4 text-white" />}
                </div>
              </div>

              {/* Contenu principal */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-3">
                    <MapPin className="w-5 h-5 text-emerald-600 mr-2" />
                    <h4 className="text-lg font-bold text-gray-800">{zone.name}</h4>
                  </div>

                  {/* Informations de prix */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center p-3 bg-emerald-50 rounded-lg">
                      <Euro className="w-4 h-4 text-emerald-600 mr-2" />
                      <div>
                        <p className="text-xs text-gray-600">Frais de base</p>
                        <p className="text-sm font-bold text-emerald-700">{zone.baseFee} €</p>
                      </div>
                    </div>
                    <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                      <Clock className="w-4 h-4 text-blue-600 mr-2" />
                      <div>
                        <p className="text-xs text-gray-600">Transporteur</p>
                        <p className="text-sm font-bold text-blue-700">
                          {zone?.transporteurName || "KASSARMOU"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Pays couverts */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">Pays couverts :</p>
                    <div className="flex flex-wrap gap-2">
                      {zone.countries?.map((country, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 rounded-full text-xs font-medium border border-green-200"
                        >
                          {country}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Overlay de sélection */}
              <div
                className={`absolute inset-0 rounded-xl transition-all duration-300 pointer-events-none ${
                  isSelected
                    ? "bg-emerald-600/5 border-emerald-500"
                    : "bg-transparent"
                }`}
              />
            </div>
          );
        })}
      </div>

      {/* Indicateur de statut */}
      <div className="mt-6">
        {renderStatusIndicator()}
      </div>
    </div>
  );
};

export default ShippingZonesDropdown;
